import type { Distance, Point } from "@pdfking/math"
import { Acos, Hypot, Max, Min, Scale, Subtract, Tan, Total } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import type { PathPoint } from "@pdfking/renderer-api"
import { DrawPath } from "@pdfking/renderer-api"

import type { FolderCorner } from "#internal"
import type { FolderPath } from "#public"

// `p` moved `length` along the unit vector `u`.
const move = (
  p: Point<Distance>,
  u: { x: number; y: number },
  length: Distance
): Point<Distance> => ({
  x: Total(p.x, Scale(length, u.x)),
  y: Total(p.y, Scale(length, u.y)),
})

/** The closed path round `corners`, each rounded as asked unless its edges
 * are too short for it, then as round as they leave room for — two corners
 * sharing an edge share it in proportion —: an arc's handles 4/3·tan(θ/4)·r
 * along its tangents. */
export const FolderOutline = (corners: FolderCorner[]): FolderPath => {
  const count = corners.length
  const at = (i: number) => corners[(i + count) % count]!
  const unit = (a: FolderCorner, b: FolderCorner) => {
    const length = Hypot(Subtract(b.x, a.x), Subtract(b.y, a.y)) as Distance
    return { x: (b.x - a.x) / length, y: (b.y - a.y) / length, length }
  }
  const turns = corners.map((corner, i) => {
    const [into, out] = [unit(at(i - 1), corner), unit(corner, at(i + 1))]
    return { into, out, turn: Acos(Max(-1, Min(1, into.x * out.x + into.y * out.y))) }
  })
  // How far each arc runs along its edges.
  const wanted = corners.map(({ radius }, i) => Scale(radius, Tan(Scale(turns[i]!.turn, 0.5))))
  const room = (i: number) => {
    const both = Total(wanted[i]!, wanted[(i + 1) % count]!)
    return both > turns[i]!.out.length ? turns[i]!.out.length / both : 1
  }
  const points = corners.flatMap((corner, i): PathPoint[] => {
    const { into, out, turn } = turns[i]!
    const reach = Scale(wanted[i]!, Min(room(i), room((i - 1 + count) % count)))
    if (reach <= ZERO_DISTANCE) return DrawPath([corner])
    const handle = Scale(reach, ((4 / 3) * Tan(Scale(turn, 0.25))) / Tan(Scale(turn, 0.5)))
    const [from, to] = [move(corner, into, Scale(reach, -1)), move(corner, out, reach)]
    return [
      { ...from, before: from, after: move(from, into, handle) },
      { ...to, before: move(to, out, Scale(handle, -1)), after: to },
    ]
  })
  return { points, closed: true }
}
